/**
 * 밴드 스파크라인 기하(순수 계산). 에피소드 위상 그리드(min/max/mean)와 컬렉션 기준 밴드
 * (p10/median/p90)를 dim 별 스파크라인 수치로 합친다. SVG path 변환은 컴포넌트
 * (`EpisodeParquetDeviationVisual`)가 한다.
 *
 * 위상 정합. 에피소드 그리드와 밴드는 같은 행 위상 좌표(0→1, `resamplePoints` 길이)에 있어야
 * 겹칠 수 있다. `resamplePoints` 가 다르면 잘라서 겹치지 않고 **밴드 전체를 숨긴다**. 잘못 겹친
 * 밴드는 없는 것보다 나쁘다. 채널이 밴드에 없거나 dim 수가 다르면 그 채널만 밴드 없이 표시한다.
 */

import {
  dimDeviation,
  deviationSeverity,
  pooledDeviation,
  rotationAngles,
  type DeviationSeverity,
  type DimDeviation,
} from "./bandDeviation";
import type {
  EpisodePhaseGrid,
  ReferenceBandGrid,
  ReferenceChannelBand,
} from "./diagnosticsSeries";
import {
  GROUP_ORDER,
  MAX_DIMS_PER_COLUMN,
  channelDimTitle,
  channelTitle,
  renderGroup,
  type ChannelStructureIndex,
  type ParquetChannelGroupId,
} from "./parquetChannels";

// 도메인 여백 비율.
const DOMAIN_PAD = 0.12;

export type BandSparkline = {
  kind: "component" | "rotation";
  key: string; // `${channel}#${dim}`
  label: string;
  // 기준 밴드. 성분은 p10/median/p90, 회전각은 0/median/p90 이다. 없으면 빈 배열이다.
  low: number[];
  median: number[];
  high: number[];
  // 에피소드 성분은 min/max 밴드와 mean 선을 가진다. 회전각은 대표 자세의 각도 선만 가진다.
  epMin: number[];
  epMax: number[];
  epMean: number[];
  domainMin: number;
  domainMax: number;
  hasBand: boolean;
  // 이 dim 의 mean 이 밴드 밖인 점의 비율. 밴드가 없거나 비교점이 0 이면 null 이다.
  outsideFraction: number | null;
  severity: DeviationSeverity;
};

export type BandColumn = {
  field: string; // 채널명(namespace 포함)
  title: string; // namespace 를 뗀 표시명. 같은 이름이 topic 마다 나오므로 group 과 함께 읽어야 한다
  // 렌더 그룹(Action/Observation/Other). 정렬 기준이고 **머리글 기준**이다. 이 값이 없으면 같은
  // 이름의 action·observation 채널이 구분 없이 두 번 그려진다.
  group: ParquetChannelGroupId;
  sparklines: BandSparkline[];
  // 컬럼(채널) pooled 이탈률. dim 평균이 아니라 Σoutside/Σcompared 다.
  outsideFraction: number | null;
  severity: DeviationSeverity;
  // dim cap 으로 잘렸으면 원래 dim 수다. 아니면 null 이다.
  truncatedFrom: number | null;
};

/**
 * 에피소드 위상 그리드와 기준 밴드로 컬럼별 밴드 스파크라인을 만든다.
 * 채널을 `GROUP_ORDER`(observation → action → other) 순으로 정렬한다.
 */
export function buildBandColumns(
  grid: EpisodePhaseGrid,
  band: ReferenceBandGrid | null,
  channels: ChannelStructureIndex,
): BandColumn[] {
  // 위상 점 수가 다르면 겹칠 수 없다. 그래서 밴드 전체를 숨긴다.
  const usableBand = band !== null && band.resamplePoints === grid.resamplePoints ? band : null;

  const columns: BandColumn[] = [];
  for (const [channel, channelGrid] of grid.channels) {
    const structure = channels.get(channel);
    const title = channelTitle(channel, structure);
    const fullDims = channelGrid.min.length;
    const isVector = fullDims > 1;
    const channelBand = usableChannelBand(usableBand?.channels.get(channel), fullDims);
    const rotation = channelBand?.rotation;
    const dims = Math.min(rotation === undefined ? fullDims : 3, MAX_DIMS_PER_COLUMN);

    const sparklines: BandSparkline[] = [];
    const dimDeviations: DimDeviation[] = [];
    for (let d = 0; d < dims; d += 1) {
      const built = buildSparkline(
        `${channel}#${d}`,
        isVector ? channelDimTitle(channel, structure, d) : title,
        {
          epMin: channelGrid.min[d] ?? [],
          epMax: channelGrid.max[d] ?? [],
          epMean: channelGrid.mean[d] ?? [],
        },
        channelBand === null
          ? null
          : {
              low: channelBand.low[d] ?? [],
              median: channelBand.median[d] ?? [],
              high: channelBand.high[d] ?? [],
            },
      );
      sparklines.push(built.sparkline);
      dimDeviations.push(built.deviation);
    }
    if (rotation !== undefined) {
      const angles = rotationAngles(channelGrid.mean.slice(3), rotation.referenceWxyz);
      const built = buildSparkline(
        channel + "#rotation",
        "회전각 (rad)",
        { epMin: [], epMax: [], epMean: angles },
        {
          // 회전은 중심에 가까울수록 범위 안이다. p10 미달을 이탈로 세지 않는다.
          low: rotation.high.map((radius) => (Number.isFinite(radius) ? 0 : Number.NaN)),
          median: rotation.median,
          high: rotation.high,
        },
      );
      built.sparkline.kind = "rotation";
      sparklines.push(built.sparkline);
      dimDeviations.push(built.deviation);
    }
    // 컬럼 pooled 값은 dim 별 분자·분모를 합쳐 계산한다. fraction 평균이 아니다(backend 와 같다).
    const outsideFraction = pooledDeviation(dimDeviations);
    columns.push({
      field: channel,
      title,
      sparklines,
      outsideFraction,
      severity: outsideFraction === null ? "ok" : deviationSeverity(outsideFraction),
      truncatedFrom: fullDims > MAX_DIMS_PER_COLUMN ? fullDims : null,
      group: renderGroup(structure, channel),
    });
  }
  // group 순으로 정렬한다(stable). 그룹 안의 등장 순서는 유지한다.
  return columns.sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));
}

/** 채널 밴드의 dim 수가 에피소드와 다르면(스키마 변형) 그 채널 밴드는 쓸 수 없다. */
function usableChannelBand(
  channelBand: ReferenceChannelBand | undefined,
  episodeDims: number,
): ReferenceChannelBand | null {
  if (channelBand === undefined || channelBand.low.length !== episodeDims) {
    return null;
  }
  return channelBand;
}

type EpisodeDimSeries = {
  epMin: readonly number[];
  epMax: readonly number[];
  epMean: readonly number[];
};
type BandDimSeries = { low: readonly number[]; median: readonly number[]; high: readonly number[] };

function buildSparkline(
  key: string,
  label: string,
  episode: EpisodeDimSeries,
  band: BandDimSeries | null,
): { sparkline: BandSparkline; deviation: DimDeviation } {
  if (band === null) {
    const { min, max } = domainOf([episode.epMin, episode.epMax, episode.epMean]);
    return {
      sparkline: {
        kind: "component",
        key,
        label,
        low: [],
        median: [],
        high: [],
        epMin: [...episode.epMin],
        epMax: [...episode.epMax],
        epMean: [...episode.epMean],
        domainMin: min,
        domainMax: max,
        hasBand: false,
        outsideFraction: null,
        severity: "ok",
      },
      deviation: { outside: 0, compared: 0, fraction: null },
    };
  }

  const deviation = dimDeviation(episode.epMean, band.low, band.high);
  const { min, max } = domainOf([
    episode.epMin,
    episode.epMax,
    episode.epMean,
    band.low,
    band.high,
  ]);
  return {
    sparkline: {
      kind: "component",
      key,
      label,
      low: [...band.low],
      median: [...band.median],
      high: [...band.high],
      epMin: [...episode.epMin],
      epMax: [...episode.epMax],
      epMean: [...episode.epMean],
      domainMin: min,
      domainMax: max,
      hasBand: band.low.some((value, i) => Number.isFinite(value) && Number.isFinite(band.high[i])),
      outsideFraction: deviation.fraction,
      severity: deviation.fraction === null ? "ok" : deviationSeverity(deviation.fraction),
    },
    deviation,
  };
}

// 여러 수치 배열이 공유하는 y 도메인(비유한 제외, 12% 패딩). 유효값이 없으면 [0,1] 이다.
function domainOf(series: readonly (readonly number[])[]): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const arr of series) {
    for (const v of arr) {
      if (Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  if (min === Infinity || max === -Infinity) {
    return { min: 0, max: 1 };
  }
  if (min === max) {
    return { min: min - 1, max: max + 1 };
  }
  const pad = (max - min) * DOMAIN_PAD;
  return { min: min - pad, max: max + pad };
}
